import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface GermanPhoneInputProps {
	id: string;
	value?: string;
	onChange: (value: string) => void;
	error?: string | undefined;
	label?: string;
	required?: boolean;
	className?: string;
}

export default function GermanPhoneInput({
	id,
	value,
	onChange,
	error,
	label,
	required = false,
	className = "h-11",
}: GermanPhoneInputProps) {
	const { t } = useTranslation();
	const [inputValue, setInputValue] = useState("");

	// Extract the digits from the phone number (remove +49 prefix if present)
	const extractDigits = (phone?: string) => {
		if (!phone) return "";
		// Remove +49 and any spaces/non-digits, then take first 15 digits
		return phone
			.replace(/^\+49\s*/, "")
			.replace(/\D/g, "")
			.slice(0, 15);
	};

	// Initialize input value from props
	useEffect(() => {
		setInputValue(extractDigits(value));
	}, [value]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const rawInput = e.target.value.replace(/\D/g, ""); // Only allow digits
		const digits = rawInput.slice(0, 15); // Limit to 15 digits
		setInputValue(digits);

		// Create the full phone number with +49 prefix
		const fullPhone = digits ? `+49${digits}` : "";
		onChange(fullPhone);
	};

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		// Move cursor to end when focused
		setTimeout(() => {
			e.target.setSelectionRange(inputValue.length, inputValue.length);
		}, 0);
	};

	const displayLabel = label || t("phone_input.label");

	return (
		<div className="space-y-2">
			<Label htmlFor={id} className="text-sm font-medium">
				{displayLabel}
				{required && <span className="text-destructive ml-1">*</span>}
				<span className="text-muted-foreground font-normal ml-1">
					{t("phone_input.recommended")}
				</span>
			</Label>
			<div className="relative">
				<div className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium pointer-events-none">
					+49
				</div>
				<Input
					id={id}
					type="tel"
					value={inputValue}
					onChange={handleInputChange}
					onFocus={handleFocus}
					placeholder={t("phone_input.placeholder")}
					className={`pl-12 ${className} ${error ? "border-destructive" : ""}`}
					maxLength={15}
				/>
			</div>
			<p className="text-xs text-muted-foreground">{t("phone_input.helper")}</p>
			{error && <p className="text-sm text-destructive mt-1">{error}</p>}
		</div>
	);
}
